import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PermitLetter } from '../../types';
import Input from '../Form/Input';
import Button from '../Form/Button';
import RichTextEditor from '../Form/RichTextEditor';
import { resolveFileUrl } from '../../utils/fileUrl';

interface CarLetterFormProps {
  letter?: PermitLetter;
  onSubmit: (data: Partial<PermitLetter> & { logoFile?: File }) => void;
  onCancel: () => void;
}

const CarLetterForm: React.FC<CarLetterFormProps> = ({ letter, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm<PermitLetter>({
    defaultValues: letter || {
      templateName: '',
      subject: '',
      content: '',
      language: 'ar',
      isDefault: false,
      logoPosition: 'top',
      logoSize: 'medium'
    }
  });
  const language = watch('language') || 'ar';
  const [content, setContent] = useState(letter?.content || '');
  const [headerHtml, setHeaderHtml] = useState(letter?.headerHtml || '');
  const [footerHtml, setFooterHtml] = useState(letter?.footerHtml || '');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [contentError, setContentError] = useState('');

  useEffect(() => {
    if (letter) {
      reset(letter);
      setContent(letter.content || '');
      setHeaderHtml(letter.headerHtml || '');
      setFooterHtml(letter.footerHtml || '');
      setLogoFile(null);
    }
  }, [letter, reset]);

  const submitWithContent = (data: PermitLetter) => {
    const plain = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    if (!plain) {
      setContentError('Content is required');
      return;
    }
    setContentError('');
    onSubmit({ ...data, content, headerHtml, footerHtml, logoFile: logoFile || undefined });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {letter ? 'Edit Car Letter Template' : 'Create New Car Letter Template'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit(submitWithContent)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Template Name */}
            <div>
              <Input
                label="Template Name"
                {...register('templateName', { required: 'Template name is required' })}
                error={errors.templateName?.message}
                placeholder="Enter template name"
              />
            </div>

            {/* Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Letter Language (لغة الخطاب)
              </label>
              <select
                {...register('language')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="ar">العربية — Arabic</option>
                <option value="en">English — الإنجليزية</option>
              </select>
            </div>

            {/* Default Template Checkbox */}
            <div className="flex items-center md:col-span-2">
              <input
                type="checkbox"
                {...register('isDefault')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Set as default template
              </label>
            </div>

            {/* Subject */}
            <div className="md:col-span-2">
              <Input
                label="Subject"
                {...register('subject', { required: 'Subject is required' })}
                error={errors.subject?.message}
                placeholder="Enter letter subject"
              />
            </div>

            {/* Content */}
            <div className="md:col-span-2">
              <RichTextEditor
                label="Letter Content (نص الخطاب)"
                value={content}
                onChange={setContent}
                error={contentError}
                dir={language === 'en' ? 'ltr' : 'rtl'}
                placeholders={[
                  { token: '{{CAR_DETAILS}}', label: 'بيانات السيارات' },
                  { token: '{{UNIT_NAME}}', label: 'اسم الوحدة / الفرع' },
                  { token: '{{DATE}}', label: 'التاريخ' },
                  { token: '{{COMPANY_NAME}}', label: 'اسم الشركة' },
                ]}
              />
              <p className="mt-2 text-sm text-gray-500">
                اكتب الخطاب كما سيُطبع. الأزرار الزرقاء تُدرج قيمًا تلقائية: بيانات السيارات المختارة، اسم الوحدة/الفرع المختار، تاريخ اليوم، واسم الشركة. الزر يمكن تكراره في أكثر من موضع.
              </p>
            </div>

            {/* Print layout: logo + header + footer (optional) */}
            <div className="md:col-span-2 border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">
                Print Layout — Letterhead &amp; Footer (اختياري — ترويسة وتذييل الخطاب)
              </h3>
              <p className="text-xs text-gray-500">
                Leave empty to print the default PetroMarine header and no footer — اتركها فارغة لاستخدام الترويسة الافتراضية.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Logo (لوجو الشركة)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                {(logoFile || letter?.logoUrl) && (
                  <img
                    src={logoFile ? URL.createObjectURL(logoFile) : resolveFileUrl(letter?.logoUrl)}
                    alt="Letter logo"
                    className="mt-2 h-16 object-contain border border-gray-200 rounded p-1 bg-white"
                  />
                )}
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Logo Position (مكان اللوجو)
                    </label>
                    <select
                      {...register('logoPosition')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="top">Centered above text — فوق النص</option>
                      <option value="right">Right side — يمين</option>
                      <option value="left">Left side — يسار</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Logo Size (حجم اللوجو)
                    </label>
                    <select
                      {...register('logoSize')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="small">Small — صغير</option>
                      <option value="medium">Medium — وسط</option>
                      <option value="large">Large — كبير</option>
                    </select>
                  </div>
                </div>
              </div>
              <RichTextEditor
                label="Letter Header — prints above the letter (ترويسة الخطاب)"
                value={headerHtml}
                onChange={setHeaderHtml}
                dir={language === 'en' ? 'ltr' : 'rtl'}
                minHeight={80}
              />
              <RichTextEditor
                label="Letter Footer — prints at the bottom (تذييل الخطاب)"
                value={footerHtml}
                onChange={setFooterHtml}
                dir={language === 'en' ? 'ltr' : 'rtl'}
                minHeight={60}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {letter ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CarLetterForm;